import { PAYMENT_QR_FILE_PREFIX } from '../constants/bookingConst';
import { downloadImageUrl } from './imageExportUtil';
import { logAuditEvent, AUDIT_ACTION_TYPES, GUEST_ACTOR } from './auditLogUtil';

/**
 * File name (without extension) for a saved payment QR, named after the
 * payment method so a guest can tell two saved QRs apart in their gallery:
 * "GCash - Main" -> "payment-qr-gcash-main".
 * @param {string} methodName - the QR's name as the admin entered it
 * @returns {string}
 */
export const buildPaymentQrFileName = (methodName) => {
  const slug = String(methodName || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug ? `${PAYMENT_QR_FILE_PREFIX}-${slug}` : PAYMENT_QR_FILE_PREFIX;
};

/**
 * Saves a payment QR to the guest's device and records it on the audit
 * trail.
 *
 * The audit write is fire-and-forget (logAuditEvent never throws), so a
 * failed log can never stop the guest from getting their QR.
 *
 * @param {object} params
 * @param {{name: string, url: string}} params.method - the QR being saved
 * @param {{entityType: string, entityId: string, performedBy?: string, summary?: object}|null} [params.audit]
 *   what the download belongs to (a booking or an Open Play join); null skips the log
 * @returns {Promise<void>}
 * @throws {Error} when the image can't be read for download
 */
export const downloadPaymentQr = async ({ method, audit = null }) => {
  await downloadImageUrl(method?.url, buildPaymentQrFileName(method?.name));
  if (!audit?.entityType || !audit?.entityId) return;
  logAuditEvent({
    actionType: AUDIT_ACTION_TYPES.PAYMENT_QR_DOWNLOADED,
    entityType: audit.entityType,
    entityId: audit.entityId,
    performedBy: audit.performedBy || GUEST_ACTOR,
    summary: { type: 'paymentQr', qrName: method?.name || '', ...(audit.summary || {}) },
  });
};
